import { FormHelperText } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import InputBase from '@mui/material/InputBase';
import { ChangeEvent, ChangeEventHandler, FC, MouseEventHandler, ReactElement, useEffect, useState } from 'react';
import { PatternFormat } from 'react-number-format';
import styled from 'styled-components';

import color from 'atoms/colors/colors';

import Text from 'quarks/Text';
import { BasicProps, basic } from 'quarks/interpolations/basic';

import Tooltip, { TooltipProps } from 'molecules/Tooltip/Tooltip';

const StyledFormControl = styled(FormControl)`
  && {
    ${basic}
  }
`;

const StyledInput = styled(InputBase)`
  && {
    ${basic}
    border: 1px solid #D0D5DD;
    @media (hover: hover) {
      &:hover {
        border: 1px solid ${color.green[300]};
        box-shadow: none;
      }
    }

    &.Mui-focused {
      border: 1px solid ${color.green[300]};
      box-shadow: none;
    }
    &.Mui-error {
      border: 1px solid ${color.sunset[400]};
      box-shadow: none;
    }
  }
`;

export interface TextFieldProps extends BasicProps {
  /**
   * Defines the icon to be used at the start of the TextField.
   */
  startIcon?: ReactElement;
  /**
   * Defines the icon to be used at the end of the TextField.
   */
  endIcon?: ReactElement;
  /**
   * The label content.
   */
  label?: string;
  /**
   * 	The short hint displayed in the `TextField` before the user enters a value.
   */
  placeholder?: string;
  /**
   * Name attribute of the `TextField` element.
   */
  name?: string;
  /**
   * Defines a string of text located under the TextField to assist the user with the TextField functionality.
   */
  helperText?: string;
  /**
   * Callback fired when the endIcon is clicked.
   *  **Signature:**
   *  `function(event: object) => void`
   */
  onEndIconClick?: MouseEventHandler<HTMLDivElement> | undefined;
  /**
   * 	Callback fired when the value is changed.
   *  **Signature:**
   *  `function(event: object) => void`
   *  event: The event source of the callback. You can pull out the new value by accessing `event.target.value` (string).
   */
  onChange?: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> | undefined;
  /**
   * 	Callback fired when the input field loses focus.
   *  **Signature:**
   *  `function(event: object) => void`
   *  event: The event source of the callback. You can pull out the new value by accessing `event.target.value` (string).
   */
  onBlur?: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> | undefined;
  /**
   * Defines the type of text TextField or a number TextField
   */
  type: 'password' | 'text' | 'email' | 'number' | 'tel';
  /**
   * 	If `true`, the component is disabled.
   */
  disabled?: boolean;
  /**
   * displays error message under the label (replaces helper text)
   */
  errorMessage?: string;
  /**
   * If `true`, the label is displayed in an error state.
   */
  error?: boolean;
  /**
   * If `true`, the label is displayed as required and the `TextField` element is required.
   */
  required?: boolean;
  /**
   * The id of the `TextField` element. Use this prop to make `label` and `helperText` accessible for screen readers.
   */
  id?: string;
  /**
   * The default value of the `TextField` element, required for a controlled component.
   */
  defaultValue?: string | boolean;
  /**
   * String that displays the title of a tooltip on the `endIcon`.
   */
  tooltip?: TooltipProps['title'];
  /**
   * Tooltip placement in regards to the endIcon. Default `top`
   */
  tooltipPlacement?: TooltipProps['placement'];
  /**
   * Rendered below the title. Used when more than a single line or phrase is needed in the tooltip.
   */
  tooltipDescription?: TooltipProps['description'];
  /**
   * if user clicks the submit button, it sets true
   */
  submitValidation?: boolean;
}

const TextField: FC<TextFieldProps> = ({
  startIcon,
  endIcon,
  label,
  placeholder,
  value,
  defaultValue,
  name,
  type,
  fontSize,
  fontWeight,
  disabled,
  required,
  id,
  tooltip,
  tooltipPlacement,
  tooltipDescription,
  onEndIconClick,
  error,
  errorMessage,
  helperText,
  submitValidation,
  onChange,
  onBlur,
  backgroundColor,
  ...props
}) => {
  const [isError, setIsError] = useState<boolean>(false);
  const [currValue, setCurrValue] = useState<string>(defaultValue || '');

  useEffect(() => {
    if (value !== undefined) {
      setCurrValue(value);
    }
  }, [value]);

  useEffect(() => {
    if (submitValidation && required && currValue === '') {
      setIsError(true);
    }
  }, [submitValidation]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    if (required && target.value === '') {
      setIsError(true);
    }
    setIsError(false);
    setCurrValue(target.value);
    onChange && onChange(e);
  };

  const handleBlur = (e: ChangeEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    if (required && target.value === '') {
      setIsError(true);
    }
    setIsError(false);

    onBlur && onBlur(e);
  };

  const styledInputProps = {
    value: currValue,
    defaultValue,
    backgroundColor: backgroundColor || 'common-white',
    paddingY: 4,
    id,
    name,
    type,
    disabled,
    required,
    placeholder,
    className: 'Styled-MuiTextfield',
    startAdornment: startIcon && <InputAdornment position="start">{startIcon}</InputAdornment>,
    endAdornment:
      endIcon &&
      (tooltip ? (
        <Tooltip title={tooltip} placement={tooltipPlacement} description={tooltipDescription}>
          <InputAdornment position="start" variant="outlined" onClick={onEndIconClick} css="cursor: pointer">
            {endIcon}
          </InputAdornment>
        </Tooltip>
      ) : (
        <InputAdornment position="start" variant="outlined" onClick={onEndIconClick} css="cursor: pointer">
          {endIcon}
        </InputAdornment>
      )),
  };

  return (
    <StyledFormControl
      variant="standard"
      error={error || isError}
      onChange={handleChange}
      onBlur={handleBlur}
      {...props}
    >
      {label && (
        <Text fontSize={fontSize} fontWeight={fontWeight} textColor={{ light: 'gray-900', dark: 'gray-900' }}>
          {label} {required ? <Text textColor="sunset-400">*</Text> : ''}
        </Text>
      )}
      {type === 'tel' ? (
        <PatternFormat type="tel" format="(###) ###-####" mask="" customInput={StyledInput} {...styledInputProps} />
      ) : (
        <StyledInput {...styledInputProps} />
      )}
      {(helperText || error) && <FormHelperText>{error ? errorMessage : helperText}</FormHelperText>}
    </StyledFormControl>
  );
};

export default TextField;

TextField.defaultProps = {
  type: 'text',
  tooltipPlacement: 'top',
  fontSize: 'textXs',
  fontWeight: 'medium',
};
